/**
 * フォルダ選択由来のフォントファイル群を `FontFace` API に登録する
 * Phase C の中核ユーティリティ。
 *
 * 設計参照:
 * - spec §5.5（`loadFontFolder` / DEFAULT_FONTS + userFonts の合流）
 * - spec §7（Font Library セクション）
 * - D1: `loadFontFolder` は DOM / FontFace API に触れるため **UI 層の責務**。
 *   state 層は結果 `FontSpec[]` を保持するだけ。
 *
 * 副作用:
 * - `document.fonts.add(face)` を呼ぶ。失敗した個別ファイルは
 *   `Promise.allSettled` で握りつぶし、成功分だけを返す。
 * - 登録済みフォントの `document.fonts.delete` は本ファイルではやらない
 *   （OQ-17 メモ。ページリロード時の GC 対策は後続フェーズで別途）。
 *
 * `sanitizeFontBasename` は純粋関数のため state 層（`src/state/fonts.ts`）
 * が提供する（spec §5.1 依存方向準拠 / D1）。
 */

import { type FontSpec, sanitizeFontBasename } from '../state';

/**
 * 受け付けるフォントファイル拡張子（小文字比較）。
 * spec §5.5 準拠。`.ttc` は FontFace で扱えないため除外。
 */
const FONT_EXTENSIONS: readonly string[] = ['.ttf', '.otf', '.woff', '.woff2'];

/** 拡張子チェック（小文字比較）。 */
function hasFontExtension(name: string): boolean {
  const lower = name.toLowerCase();
  return FONT_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * ファイル名から拡張子を除いた basename を返す。
 * `sanitizeFontBasename` は英数字以外を `_` に畳むだけで拡張子を保つため、
 * 呼び出し側で先に拡張子を落としておく（`MyFont.otf` → `MyFont` → `MyFont`）。
 */
function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/**
 * 指定ファイル群を FontFace API に登録し、成功した分の `FontSpec` を返す。
 *
 * @param files - `<input type="file" webkitdirectory>` の `.files` でも
 *   任意の `File[]` でも可。拡張子フィルタを内部で行う。
 * @returns 成功した `FontSpec` 配列（失敗分は除外）。全滅時は空配列。
 */
export async function loadFontFolder(files: FileList | File[]): Promise<FontSpec[]> {
  // FileList / File[] を同じ扱いにする
  const arr: File[] = Array.isArray(files) ? files : Array.from(files);
  const fontFiles = arr.filter((f) => hasFontExtension(f.name));
  if (fontFiles.length === 0) return [];

  // 同じフォルダでも複数回呼ばれると family 衝突するので、呼び出しタイムスタンプ
  // と index で一意化（spec §5.5 の命名規則）。
  const stamp = Date.now();

  const tasks = fontFiles.map(async (file, i): Promise<FontSpec> => {
    const base = stripExtension(file.name);
    const safe = sanitizeFontBasename(base);
    const family = `user_${safe}_${stamp}_${i}`;
    const buffer = await file.arrayBuffer();
    const face = new FontFace(family, buffer);
    await face.load();
    document.fonts.add(face);
    return {
      // CSS `font-family` で使う際は引用符で囲む（family 内にアンダースコア
      // は許容されるが、将来 name を user 入力から生成する余地を残すため
      // 保守的に quote 込みの値にしておく）。
      family: `"${family}"`,
      // dropdown に出す表示名は元ファイル名の basename（拡張子除去版）。
      // user 起源フォントには呼び出し側で `★ ` プレフィックスを付ける。
      name: safe,
      source: 'user',
    };
  });

  const results = await Promise.allSettled(tasks);
  const ok: FontSpec[] = [];
  for (const r of results) {
    if (r.status === 'fulfilled') {
      ok.push(r.value);
    } else {
      // 失敗は黙殺せず console に残す（UI 側でユーザへのトースト通知等は
      // 後続フェーズ検討）。
      console.warn('[fontLoader] failed to load font:', r.reason);
    }
  }
  return ok;
}
